// Pure helpers for a campaign's teasers: the Add presets, worked out from the
// start in Asia/Kolkata, and the rules the server refuses a teaser by, so a
// preset that would be refused is disabled with the reason. The server stays
// the judge. No React and no requests.
import { dayTime, istDayStart, parseTs } from "./campaignMedia";

const IST_OFFSET_MS = 330 * 60000; // Asia/Kolkata, the program timezone. No DST.
const DAY_MS = 86400000;
const HOUR_MS = 3600000;

/** How many a campaign may have, its own teaser included. The list response carries the real number. */
export const TEASER_LIMIT = 3;
/** Pushes go out 08:00 to 21:00 IST; 21:00 itself is quiet. */
export const SEND_FROM_MIN = 8 * 60;
export const SEND_UNTIL_MIN = 21 * 60;
/** Two teasers this close are refused. Exactly 12 h apart is fine. */
export const MIN_GAP_MS = 12 * HOUR_MS;

export type TeaserPreset = "3d" | "1d";
export const PRESETS: { key: TeaserPreset; label: string; daysBefore: number }[] = [
  { key: "3d", label: "3 days before, 7 pm", daysBefore: 3 },
  { key: "1d", label: "1 day before, 7 pm", daysBefore: 1 },
];
const PRESET_HOUR = 19;

/** 7 pm IST, `daysBefore` days before the IST day the campaign starts on.
 *  Starts Fri 6 Nov 00:00 IST (still Thu 5 Nov in UTC), 3 days -> Tue 3 Nov 19:00 IST. */
export const presetAt = (startsAt: string, daysBefore: number): number =>
  istDayStart(parseTs(startsAt)) - daysBefore * DAY_MS + PRESET_HOUR * HOUR_MS;

/** Minutes past 00:00 IST. */
export const istMinutes = (ms: number): number =>
  Math.floor(((((ms + IST_OFFSET_MS) % DAY_MS) + DAY_MS) % DAY_MS) / 60000);

export const inSendHours = (ms: number): boolean => {
  const m = istMinutes(ms);
  return m >= SEND_FROM_MIN && m < SEND_UNTIL_MIN;
};

export interface TeaserContext {
  /** the stored start, naive UTC: what the server judges against */
  startsAt: string;
  now: number;
  /** send_at of every teaser already there, the campaign's own and sent ones included */
  taken: string[];
  limit: number;
}

/** Why the server would refuse a teaser at `at`, or null when it should take it. */
export const teaserProblem = (at: number, c: TeaserContext): string | null => {
  if (!isFinite(at)) return "Enter a date and time";
  if (c.taken.length >= c.limit) return `${c.limit} of ${c.limit} already`;
  if (at <= c.now) return "In the past";
  if (at >= parseTs(c.startsAt)) return "After the start";
  if (!inSendHours(at)) return "Outside 8 am–9 pm IST";
  const near = c.taken.map(parseTs)
    .filter((t) => Math.abs(t - at) < MIN_GAP_MS)
    .sort((a, b) => Math.abs(a - at) - Math.abs(b - at))[0];
  if (near != null) return `Within 12 h of ${dayTime(near)}`;
  return null;
};

export interface PresetOption { key: TeaserPreset; label: string; at: number; problem: string | null; }

export const presetOptions = (c: TeaserContext): PresetOption[] =>
  PRESETS.map((p) => {
    const at = presetAt(c.startsAt, p.daysBefore);
    return { key: p.key, label: p.label, at, problem: teaserProblem(at, c) };
  });
